import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";

export default function PaymentSuccess() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | error | timeout
  const attempts = useRef(0);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("error"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (attempts.current >= 10) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* continue polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-accent" />
            <h1 className="mt-5 font-heading font-extrabold text-2xl">{t("pay.checking")}</h1>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h1 className="mt-5 font-heading font-extrabold text-3xl">{t("pay.successTitle")}</h1>
            <p className="mt-3 text-muted-foreground">{t("pay.successDesc")}</p>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 p-3">
              <ShieldCheck className="h-4 w-4" /> {t("pay.protectedNote")}
            </div>
          </div>
        )}
        {state === "timeout" && (
          <div data-testid="payment-timeout">
            <AlertTriangle className="mx-auto h-12 w-12 text-amber-500" />
            <h1 className="mt-5 font-heading font-extrabold text-2xl">{t("pay.checking")}</h1>
            <p className="mt-3 text-muted-foreground">{t("pay.timeoutDesc")}</p>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
            <h1 className="mt-5 font-heading font-extrabold text-2xl">{t("pay.errorTitle")}</h1>
            <p className="mt-3 text-muted-foreground">{t("pay.errorDesc")}</p>
          </div>
        )}
        <Button data-testid="back-to-dashboard-btn" className="mt-8 rounded-sm bg-primary text-white" onClick={() => navigate("/dashboard")}>
          {t("pay.backDash")}
        </Button>
      </div>
    </div>
  );
}
